import gql from 'graphql-tag';
import { fragments as addressFragments } from './address';

const fragments = {
  cartInfos: gql`
    fragment CartInfosFragment on CartV2 {
      id
      checkoutUrl
      currencyCode
      subTotal
      taxTotal
      total
      totalItems
      shippingTotal
      orderPromotionTotal
      checkoutState
      totalPfand
      subscriptionId
    }
  `,
  attributes: gql`
    fragment AttributesFragment on CartV2 {
      source
      isFresh
      isProspecting
      isRoundUpApplied
      roundUpAmount
      withMembership
      hasUltraFreshProducts
      checkoutOrigin
    }
  `,
  items: gql`
    fragment CartItemFragment on CartItemV2 {
      name
      quantity
      unitPrice
      sku
      variantCode
      variantOriginalPrice
      variantWeight
      variantShippingRequired
      subtotal
      productVendor
      productImage
      total {
        amount
        currencyCode
      }
    }
  `,
  payments: gql`
    fragment PaymentFragment on Payment {
      id
      financialPaymentIntentClientSecret
      paypalOrderId
      financialPaymentRedirectLink
      financialPaymentIntentId
    }
  `,
  shipments: gql`
    fragment ShipmentFragment on Shipment {
      code
      id
      pointId
      deliveryDate
      timeSlot
      deliveryInstruction
      type
      address {
        id
        lastName
        firstName
        phoneNumber
        company
        countryCode
        provinceCode
        street
        city
        postcode
        hash
      }
      translations {
        fr_FR {
          name
        }
        de_DE {
          name
        }
      }
    }
  `,
  coupon: gql`
    fragment CouponFragment on PromotionCoupon {
      id
      code
    }
  `,
};

export const CREATE_CHECKOUT_CART = gql`
  mutation CreateCart(
    $cart: ClientCart!
    $attributes: CartAttributesInput
    $validate: Boolean
  ) {
    createCart(cart: $cart, attributes: $attributes, validate: $validate) {
      cart {
        ...CartInfosFragment
        ...AttributesFragment
        items {
          ...CartItemFragment
        }
        payment {
          ...PaymentFragment
        }
        shipments {
          ...ShipmentFragment
        }
        shippingAddress {
          ...AddressFragment
        }
        billingAddress {
          ...AddressFragment
        }
        promotionCoupon {
          ...CouponFragment
        }
        ...AttributesFragment
      }
      notifications {
        type
        translatedMessage
        details {
          sku
        }
      }
      errors {
        sku
        error
        message
      }
    }
  }

  ${fragments.cartInfos}
  ${fragments.attributes}
  ${fragments.items}
  ${fragments.payments}
  ${fragments.shipments}
  ${addressFragments.address}
  ${fragments.coupon}
  ${fragments.attributes}
`;

export const GET_COUNTRIES = gql`
  query getCountries {
    getCountries {
      code
      provinces
    }
  }
`;

export const GET_CHANNEL_RESOURCE = gql`
  query getChannelResource($code: String!) {
    getChannelResource(code: $code) {
      countries {
        code
      }
    }
  }
`;

export const GET_CHANNEL_PAYMENT_METHODS = gql`
  query getChannelResource($code: String!) {
    getChannelResource(code: $code) {
      paymentMethods {
        code
        type
        psp
        method
        publishableKey
      }
    }
  }
`;

export const ADD_SHIPPING_AND_BILLING_ADDRESS = gql`
  mutation addShippingAndBillingAddresses(
    $tokenValue: String!
    $billingAddress: OmsAddress!
    $shippingAddress: OmsAddress!
  ) {
    addShippingAndBillingAddresses(
      tokenValue: $tokenValue
      billingAddress: $billingAddress
      shippingAddress: $shippingAddress
    ) {
      ...CartInfosFragment
      billingAddress {
        ...AddressFragment
      }
      shippingAddress {
        ...AddressFragment
      }
      shipments {
        ...ShipmentFragment
      }
    }
  }

  ${fragments.cartInfos}
  ${addressFragments.address}
  ${fragments.shipments}
`;

export const ADD_BILLING_ADDRESS = gql`
  mutation addBillingAddress(
    $tokenValue: String!
    $billingAddress: OmsAddress!
  ) {
    addBillingAddress(
      tokenValue: $tokenValue
      billingAddress: $billingAddress
    ) {
      ...CartInfosFragment
      billingAddress {
        ...AddressFragment
      }
      shipments {
        ...ShipmentFragment
      }
    }
  }

  ${fragments.cartInfos}
  ${addressFragments.address}
  ${fragments.shipments}
`;

export const LIST_SHIPPING_METHODS = gql`
  query listShippingMethods($tokenValue: String!) {
    listShippingMethods(tokenValue: $tokenValue) {
      id
      code
      tmsCode
      price
      type
      translations {
        fr_FR {
          name
          description
        }
        de_DE {
          name
          description
        }
      }
    }
  }
`;

export const SELECT_HOME_SHIPPING_METHOD = gql`
  mutation selectHomeShippingMethod(
    $tokenValue: String!
    $shipmentId: String!
    $shipmentMethodCode: String!
    $deliveryInstruction: String
    $addressId: String
  ) {
    selectHomeShippingMethod(
      tokenValue: $tokenValue
      shipmentId: $shipmentId
      shipmentMethodCode: $shipmentMethodCode
      deliveryInstruction: $deliveryInstruction
      addressId: $addressId
    ) {
      ...CartInfosFragment
      ...AttributesFragment
      shipments {
        ...ShipmentFragment
      }
    }
  }

  ${fragments.cartInfos}
  ${fragments.attributes}
  ${fragments.shipments}
`;

export const SELECT_COURIER_SHIPPING_METHOD = gql`
  mutation selectCourierShippingMethod(
    $tokenValue: String!
    $shipmentId: String!
    $shipmentMethodCode: String!
    $deliveryDate: String!
    $timeSlot: String!
    $deliveryInstruction: String
    $addressId: String
  ) {
    selectCourierShippingMethod(
      tokenValue: $tokenValue
      shipmentId: $shipmentId
      shipmentMethodCode: $shipmentMethodCode
      deliveryDate: $deliveryDate
      timeSlot: $timeSlot
      deliveryInstruction: $deliveryInstruction
      addressId: $addressId
    ) {
      ...CartInfosFragment
      ...AttributesFragment
      shipments {
        ...ShipmentFragment
      }
    }
  }

  ${fragments.cartInfos}
  ${fragments.attributes}
  ${fragments.shipments}
`;

export const SELECT_SHIPPING_PICKUP_METHOD = gql`
  mutation selectPickupShippingMethod(
    $tokenValue: String!
    $shipmentId: String!
    $shipmentMethodCode: String!
    $pointId: String!
    $address: PickupAddress!
  ) {
    selectPickupShippingMethod(
      tokenValue: $tokenValue
      shipmentId: $shipmentId
      shipmentMethodCode: $shipmentMethodCode
      pointId: $pointId
      address: $address
    ) {
      ...CartInfosFragment
      ...AttributesFragment
      shipments {
        ...ShipmentFragment
      }
    }
  }

  ${fragments.cartInfos}
  ${fragments.attributes}
  ${fragments.shipments}
`;

export const ADD_CHECKOUT_COUPON = gql`
  mutation addCouponCode($tokenValue: String!, $couponCode: String!) {
    addCouponCode(tokenValue: $tokenValue, couponCode: $couponCode) {
      ...CartInfosFragment
      ...AttributesFragment
      promotionCoupon {
        ...CouponFragment
      }
      payment {
        ...PaymentFragment
      }
    }
  }

  ${fragments.cartInfos}
  ${fragments.attributes}
  ${fragments.coupon}
  ${fragments.payments}
`;

export const DELETE_CHECKOUT_COUPON = gql`
  mutation deleteCouponCode($tokenValue: String!) {
    deleteCouponCode(tokenValue: $tokenValue) {
      ...CartInfosFragment
      ...AttributesFragment
      promotionCoupon {
        ...CouponFragment
      }
      payment {
        ...PaymentFragment
      }
    }
  }

  ${fragments.cartInfos}
  ${fragments.attributes}
  ${fragments.coupon}
  ${fragments.payments}
`;

export const ADD_CHECKOUT_ROUND_UP = gql`
  mutation addRoundUp($tokenValue: String!) {
    addRoundUp(tokenValue: $tokenValue) {
      ...CartInfosFragment
      ...AttributesFragment
      items {
        ...CartItemFragment
      }
      payment {
        ...PaymentFragment
      }
      shipments {
        ...ShipmentFragment
      }
      shippingAddress {
        ...AddressFragment
      }
      billingAddress {
        ...AddressFragment
      }
      promotionCoupon {
        ...CouponFragment
      }
    }
  }

  ${fragments.cartInfos}
  ${fragments.attributes}
  ${fragments.items}
  ${fragments.payments}
  ${fragments.shipments}
  ${addressFragments.address}
  ${fragments.coupon}
  ${fragments.attributes}
`;

export const REMOVE_CHECKOUT_ROUND_UP = gql`
  mutation removeRoundUp($tokenValue: String!) {
    removeRoundUp(tokenValue: $tokenValue) {
      ...CartInfosFragment
      ...AttributesFragment
      items {
        ...CartItemFragment
      }
      payment {
        ...PaymentFragment
      }
      shipments {
        ...ShipmentFragment
      }
      shippingAddress {
        ...AddressFragment
      }
      billingAddress {
        ...AddressFragment
      }
      promotionCoupon {
        ...CouponFragment
      }
    }
  }

  ${fragments.cartInfos}
  ${fragments.attributes}
  ${fragments.items}
  ${fragments.payments}
  ${fragments.shipments}
  ${addressFragments.address}
  ${fragments.coupon}
  ${fragments.attributes}
`;

export const GET_CHECKOUT_CART = gql`
  query getCheckoutCart($tokenValue: String!, $refresh: Boolean) {
    getCheckoutCartWithRefresh(tokenValue: $tokenValue, refresh: $refresh) {
      cart {
        ...CartInfosFragment
        ...AttributesFragment
        items {
          ...CartItemFragment
        }
        payment {
          ...PaymentFragment
        }
        shipments {
          ...ShipmentFragment
        }
        shippingAddress {
          ...AddressFragment
        }
        billingAddress {
          ...AddressFragment
        }
        promotionCoupon {
          ...CouponFragment
        }
      }
      notifications {
        type
        translatedMessage
        details {
          sku
        }
      }
    }
  }

  ${fragments.cartInfos}
  ${fragments.attributes}
  ${fragments.items}
  ${fragments.payments}
  ${fragments.shipments}
  ${addressFragments.address}
  ${fragments.coupon}
  ${fragments.attributes}
`;

export const CREATE_PAYMENT_INTENT = gql`
  mutation createPaymentIntent(
    $tokenValue: String!
    $paymentId: String!
    $paymentMethodCode: String!
    $paymentToken: String
    $temporaryFinancialPaymentMethodToken: String
    $financialPaymentMethodId: String
    $saveCardForFutureUse: Boolean
  ) {
    createPaymentIntent(
      tokenValue: $tokenValue
      paymentId: $paymentId
      paymentMethodCode: $paymentMethodCode
      paymentToken: $paymentToken
      temporaryFinancialPaymentMethodToken: $temporaryFinancialPaymentMethodToken
      financialPaymentMethodId: $financialPaymentMethodId
      saveCardForFutureUse: $saveCardForFutureUse
    ) {
      ...CartInfosFragment
      payment {
        ...PaymentFragment
      }
    }
  }

  ${fragments.cartInfos}
  ${fragments.payments}
`;

export const CREATE_PAYMENT_INTENT_FOR_EXISTING_PM = gql`
  mutation createPaymentIntentForExistingPM(
    $tokenValue: String!
    $cartPaymentId: String!
    $paymentMethodId: String!
    $paymentMethodCode: String!
  ) {
    createPaymentIntentForExistingPM(
      tokenValue: $tokenValue
      cartPaymentId: $cartPaymentId
      paymentMethodId: $paymentMethodId
      paymentMethodCode: $paymentMethodCode
    ) {
      ...CartInfosFragment
      payment {
        ...PaymentFragment
      }
    }
  }

  ${fragments.cartInfos}
  ${fragments.payments}
`;

export const CREATE_PAYMENT_INTENT_FOR_NEW_CARD = gql`
  mutation createPaymentIntentForNewCard(
    $tokenValue: String!
    $cartPaymentId: String!
    $temporaryPaymentMethodToken: String!
    $paymentMethodCode: String!
    $saveCardForFutureUse: Boolean
  ) {
    createPaymentIntentForNewCard(
      tokenValue: $tokenValue
      cartPaymentId: $cartPaymentId
      temporaryPaymentMethodToken: $temporaryPaymentMethodToken
      paymentMethodCode: $paymentMethodCode
      saveCardForFutureUse: $saveCardForFutureUse
    ) {
      ...CartInfosFragment
      payment {
        ...PaymentFragment
      }
    }
  }

  ${fragments.cartInfos}
  ${fragments.payments}
`;

export const CREATE_PAYMENT_INTENT_FOR_NEW_APPLE_PAY_CARD = gql`
  mutation createPaymentIntentForNewApplePayCard(
    $tokenValue: String!
    $cartPaymentId: String!
    $temporaryPaymentMethodToken: String!
    $paymentMethodCode: String!
    $saveCardForFutureUse: Boolean
  ) {
    createPaymentIntentForNewApplePayCard(
      tokenValue: $tokenValue
      cartPaymentId: $cartPaymentId
      temporaryPaymentMethodToken: $temporaryPaymentMethodToken
      paymentMethodCode: $paymentMethodCode
      saveCardForFutureUse: $saveCardForFutureUse
    ) {
      ...CartInfosFragment
      payment {
        ...PaymentFragment
      }
    }
  }

  ${fragments.cartInfos}
  ${fragments.payments}
`;

export const CREATE_PAYMENT_INTENT_FOR_NEW_PAYPAL = gql`
  mutation createPaymentIntentForNewPaypal(
    $tokenValue: String!
    $cartPaymentId: String!
    $paymentMethodCode: String!
  ) {
    createPaymentIntentForNewPaypal(
      tokenValue: $tokenValue
      cartPaymentId: $cartPaymentId
      paymentMethodCode: $paymentMethodCode
    ) {
      ...CartInfosFragment
      payment {
        ...PaymentFragment
      }
    }
  }

  ${fragments.cartInfos}
  ${fragments.payments}
`;

export const CREATE_PAYMENT_INTENT_FOR_NEW_PAYPAL_AGREEMENT = gql`
  mutation createPaymentIntentForNewPaypalAgreement(
    $tokenValue: String!
    $cartPaymentId: String!
    $paypalPaymentToken: String!
    $paymentMethodCode: String!
  ) {
    createPaymentIntentForNewPaypalAgreement(
      tokenValue: $tokenValue
      cartPaymentId: $cartPaymentId
      paypalPaymentToken: $paypalPaymentToken
      paymentMethodCode: $paymentMethodCode
    ) {
      ...CartInfosFragment
      payment {
        ...PaymentFragment
      }
    }
  }

  ${fragments.cartInfos}
  ${fragments.payments}
`;

export const LIST_PAYMENT_METHODS = gql`
  query listPaymentMethods($tokenValue: String!, $paymentId: String!) {
    listPaymentMethods(tokenValue: $tokenValue, paymentId: $paymentId) {
      id
      name
      code
      publishableKey
      type
      psp
      method
    }
  }
`;

export const COMPLETE_ORDER = gql`
  mutation completeOrder(
    $tokenValue: String!
    $notes: String
    $withSubscription: Boolean
    $membershipPlan: MembershipPlan
    $subscriptionPromotionCode: String
    $paymentMetadata: PaymentMetadataInput
  ) {
    completeOrder(
      tokenValue: $tokenValue
      notes: $notes
      withSubscription: $withSubscription
      membershipPlan: $membershipPlan
      subscriptionPromotionCode: $subscriptionPromotionCode
      paymentMetadata: $paymentMetadata
    ) {
      ...CartInfosFragment
    }
  }

  ${fragments.cartInfos}
`;

export const CREATE_PAYMENT_WITH_3DS = gql`
  mutation createPaymentWith3DS($token: String!, $paymentMethodSource: String) {
    createPaymentWith3DS(
      token: $token
      paymentMethodSource: $paymentMethodSource
    ) {
      id
      redirectLink
    }
  }
`;

export const GET_PAYMENT = gql`
  query getPayment($paymentId: String!) {
    getPayment(paymentId: $paymentId) {
      paymentMethodId
    }
  }
`;

export const CREATE_PSP_TOKEN_FROM_APPLE_PAY_TOKEN = gql`
  mutation createPspTokenFromApplePayToken($token: JSONObject!) {
    createPspTokenFromApplePayToken(token: $token) {
      token
    }
  }
`;

export const CREATE_CHECKOUT_PAYMENT_SESSION = gql`
  mutation createCheckoutPaymentSession($tokenValue: String!) {
    createCheckoutPaymentSession(tokenValue: $tokenValue) {
      id
      paymentSessionToken
    }
  }
`;

export const CREATE_CHECKOUT_FLOW_PAYMENT_SESSION = gql`
  mutation createCheckoutFlowPaymentSession(
    $tokenValue: String!
    $cardSavingMode: CardSavingMode
  ) {
    createCheckoutFlowPaymentSession(
      tokenValue: $tokenValue
      cardSavingMode: $cardSavingMode
    ) {
      id
      paymentSessionToken
    }
  }
`;

export const CREATE_SUBSCRIPTION_PAYMENT_SESSION = gql`
  mutation createSubscriptionPaymentSession(
    $successUrl: String
    $failureUrl: String
  ) {
    createSubscriptionPaymentSession(
      successUrl: $successUrl
      failureUrl: $failureUrl
    ) {
      id
      paymentSessionToken
    }
  }
`;

export const CONFIRM_FLOW_PAYMENT = gql`
  mutation confirmFlowPayment(
    $tokenValue: String!
    $cartPaymentId: String!
    $paymentMethodCode: String!
    $financialPaymentIntentId: String!
    $saveCardForFutureUse: Boolean
  ) {
    confirmFlowPayment(
      tokenValue: $tokenValue
      cartPaymentId: $cartPaymentId
      paymentMethodCode: $paymentMethodCode
      financialPaymentIntentId: $financialPaymentIntentId
      saveCardForFutureUse: $saveCardForFutureUse
    ) {
      ...CartInfosFragment
      payment {
        ...PaymentFragment
        # paymentMethodType exists only on lego >= the LF-1894 lego branch — lego
        # must deploy before monofront.
        paymentMethodType
      }
    }
  }

  ${fragments.cartInfos}
  ${fragments.payments}
`;
